// src/components/auth/components/ForgotPasswordForm.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import EmailField from "./EmailField";
import { useForgotPassword } from "@/pages/auth/useForgotPassword";

type Props = {
  /**
   * Optional override – if provided, this will be called
   * instead of the default API-powered hook.submit().
   */
  onSubmit?: (email: string) => Promise<void> | void;
};

export default function ForgotPasswordForm({ onSubmit }: Props) {
  const [email, setEmail] = useState("");
  const { submit, loading, sent, error } = useForgotPassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    if (onSubmit) {
      // allow tests / stories to override behavior
      await onSubmit(trimmed);
      return;
    }

    await submit(trimmed);
  };

  if (sent) {
    return (
      <div className="mt-8 space-y-4 text-center">
        <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-700">
          ✓
        </div>
        <h2 className="text-lg font-semibold text-gray-800">
          Check your inbox
        </h2>
        <p className="text-sm text-gray-600">
          If an account exists for that email, you’ll receive a link to reset
          your password shortly.
        </p>
        <div className="pt-2 text-xs">
          <Link
            to="/login"
            className="font-medium text-sky-600 hover:text-sky-700"
          >
            Return to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
      <EmailField value={email} onChange={setEmail} autoFocus />

      {error && (
        <p className="text-xs text-red-500 mt-1 text-center">{error}</p>
      )}

      <button
        type="submit"
        className="w-full rounded-xl bg-sky-500 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-60"
        disabled={!email.trim() || loading}
      >
        {loading ? "Sending..." : "Send Reset Link"}
      </button>

      <div className="text-center text-xs">
        <Link
          to="/login"
          className="font-medium text-sky-600 hover:text-sky-700"
        >
          Back to login
        </Link>
      </div>
    </form>
  );
}
